import React, { useCallback, useEffect, useState } from 'react';
import { Layers, Plus, Pencil, Loader2, Users } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../ui-v2';
import { Table, Badge, EmptyState, Modal, Input } from '../../ui-v2.5';
import { getLotes, renombrarLote } from '../api';
import ModalNuevoLote from './ModalNuevoLote';
import ModalPersonasLote from './ModalPersonasLote';

/**
 * PanelLotes — los lotes (tandas) de una campaña, en DS v2.
 *
 * Una campaña nace con su «Lote 1» (el asistente). Con la campaña Activa o Pausada se añaden lotes
 * con otro criterio; quien ya está en la campaña, en cualquier lote, queda fuera y se dice por qué.
 * Añadir a una campaña Activa avisa sólo a los nuevos; en Pausada, al reanudar.
 *
 * Los nombres de lote los escribe un usuario: se pintan como texto, nunca como HTML.
 */

const ETIQUETA_CRITERIO = {
  negocio: 'Negocio',
  departamento: 'Departamento',
  empresa: 'Empresa',
  empresa_colaboradora: 'Empresa colaboradora',
};

export function textoCriterio(c) {
  if (!c) return 'Asistente de la campaña';
  if (c.tipo === 'manual') return `Selección manual (${c.personas})`;
  const valores = (c.valores || []).map((v) => v.nombre).join(', ');
  return `${ETIQUETA_CRITERIO[c.tipo] || c.tipo}: ${valores}`;
}

function fechaCorta(s) {
  if (!s) return null;
  return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

export default function PanelLotes({ campana, puedeGestionar, version, onCambio }) {
  const [lotes, setLotes] = useState(null);
  const [error, setError] = useState(null);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [renombrando, setRenombrando] = useState(null); // { id, numero, nombre }
  const [editandoPersonas, setEditandoPersonas] = useState(null); // lote

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setLotes(await getLotes(campana.id));
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudieron cargar los lotes.');
    }
  }, [campana.id]);

  useEffect(() => { cargar(); }, [cargar, version]);

  const enCurso = ['Activa', 'Pausada'].includes(campana.estado);

  const columnas = [
    { key: 'numero', header: 'Nº', width: '56px', render: (l) => <span className="tabular-nums">{l.numero}</span> },
    { key: 'nombre', header: 'Lote', render: (l) => <span className="font-medium text-v25-ink-900">{l.nombre}</span> },
    { key: 'criterio', header: 'Criterio', render: (l) => <span className="text-v25-ink-500">{textoCriterio(l.criterio)}</span> },
    { key: 'total', header: 'Personas', render: (l) => <span className="tabular-nums">{l.total}</span> },
    { key: 'realizados', header: 'Realizados', render: (l) => <span className="tabular-nums">{l.realizados}</span> },
    { key: 'sin_cita', header: 'Sin cita', render: (l) => <span className="tabular-nums">{l.sin_cita}</span> },
    { key: 'con_cita', header: 'Con cita', render: (l) => <span className="tabular-nums">{l.con_cita}</span> },
    { key: 'cancelados', header: 'Cancelados', render: (l) => <span className="tabular-nums">{l.cancelados}</span> },
    {
      key: 'aviso', header: 'Aviso',
      render: (l) => (l.notificado_en
        ? <Badge tone="active">Avisado {fechaCorta(l.notificado_en)}</Badge>
        : <Badge tone="waiting">Al activar</Badge>),
    },
    ...(puedeGestionar ? [{
      key: 'acciones', header: '', width: '96px',
      render: (l) => (
        <div className="flex items-center justify-end gap-1">
          {enCurso && (
            <Button variant="ghost" size="icon" title="Editar personas" onClick={() => setEditandoPersonas(l)}>
              <Users className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" title="Renombrar" onClick={() => setRenombrando({ id: l.id, numero: l.numero, nombre: l.nombre })}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <section className="mb-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <h2 className="text-base font-medium text-v25-ink-900 flex items-center gap-2">
            <Layers className="w-4 h-4" /> Lotes
          </h2>
          <p className="text-xs text-v25-ink-500">
            Una persona está una sola vez en la campaña, en cualquiera de sus lotes.
          </p>
        </div>
        {puedeGestionar && (
          enCurso ? (
            <Button size="sm" onClick={() => setNuevoAbierto(true)}>
              <Plus className="w-3.5 h-3.5" /> Añadir lote
            </Button>
          ) : (
            campana.estado === 'Borrador' && (
              <span className="text-xs text-v25-ink-500">Los lotes se añaden con la campaña en curso.</span>
            )
          )
        )}
      </div>

      {error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3">{error}</div>
      ) : lotes === null ? (
        <div className="text-sm text-v25-ink-500 py-4"><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />Cargando lotes…</div>
      ) : (
        <Table
          columns={columnas}
          rows={lotes}
          emptyState={<EmptyState variant="minimal" title="Esta campaña aún no tiene lotes." />}
        />
      )}

      {nuevoAbierto && (
        <ModalNuevoLote
          open={nuevoAbierto}
          campana={campana}
          onClose={() => setNuevoAbierto(false)}
          onCreado={() => { setNuevoAbierto(false); cargar(); onCambio?.(); }}
        />
      )}

      {editandoPersonas && (
        <ModalPersonasLote
          open={!!editandoPersonas}
          campana={campana}
          lote={editandoPersonas}
          lotes={lotes || []}
          onClose={() => setEditandoPersonas(null)}
          onCambio={() => { cargar(); onCambio?.(); }}
        />
      )}

      <ModalRenombrar
        lote={renombrando}
        campanaId={campana.id}
        onClose={() => setRenombrando(null)}
        onHecho={() => { setRenombrando(null); cargar(); onCambio?.(); }}
      />
    </section>
  );
}

function ModalRenombrar({ lote, campanaId, onClose, onHecho }) {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setNombre(lote?.nombre || ''); setError(null); }, [lote]);

  async function guardar() {
    setGuardando(true); setError(null);
    try {
      await renombrarLote(campanaId, lote.id, nombre);
      toast.success('Lote renombrado.');
      onHecho();
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo renombrar el lote.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open={!!lote}
      onClose={guardando ? () => {} : onClose}
      size="sm"
      title={lote ? `Renombrar el lote ${lote.numero}` : ''}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !nombre.trim()}>
            {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Guardar
          </Button>
        </>
      )}
    >
      <Input label="Nombre del lote" value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} error={error || undefined} />
    </Modal>
  );
}
